import { useCallback, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { Alert, Button, DialogContent, Stack, Typography } from '@mui/material';
import { RefreshCw } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import {
  Activity_Types_Enum_Enum,
  Lead_Agent_Source_Enum_Enum,
  type UserSelectFragment,
  type Users_Bool_Exp,
} from '@graphql/__generated__/graphql';
import {
  GET_LEAD_ASSIGN_STAGE_QUERY,
  GET_USER_TEAMS,
} from '@modules/_shared/common/queries';
import { ConfirmDialog } from '@modules/_shared/components/ConfirmDialog';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { UserSelect } from '@modules/_shared/components/user-select/UserSelect';
import { UserAvatar } from '@modules/_shared/components/UserAvatar';
import { getUserDisplayName } from '@modules/_shared/components/UserCard';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { isViewerOrLedUser } from '@modules/_shared/utils/isViewerOrLedUser';
import { createResultPageReassignment } from '@modules/_shared/utils/leadAgentReassignment';
import { keepsProspectionAssignmentTask } from '@modules/_shared/utils/leadAgentSources';
import { getAssignableLeadTeamIds } from '@modules/_shared/utils/leadAssignment';
import { GET_LEAD_AGENT_STATUS } from '@modules/lead-details/components/cards/LeadDetailsStatusCard';
import { GET_LEAD_DETAILS } from '@modules/lead-details/LeadDetailsDrawer';
import { LeadAgents } from '@modules/leads/LeadAgents';
import {
  ASSIGN_BROKER,
  GET_LEADS,
  GET_LEAD_AGENT_SELECT_CARD_QUERY,
} from '@modules/leads/leadsQueries';
import { mergeVisibleLeadAgents } from '@modules/leads/utils/mergeVisibleLeadAgents';
import { useLocale } from '@shared/hooks/locale';

type LeadDetailsAgentCardProps = {
  leadId: string;
  onAssignmentChange?: (keepOpen: boolean) => void;
};

const GET_TEAM_MEMBERS = gql(/* GraphQL */ `
  query GetTeamMembers($team_ids: [uuid!]!) {
    teams_users(
      where: { team_id: { _in: $team_ids }, deactivated: { _eq: false } }
    ) {
      user {
        id
        full_name
        first_name
        last_name
        primaryEmail: emails(
          order_by: [{ primary: desc }, { verified: desc }]
          limit: 1
        ) {
          email
        }
        user_images(where: { is_primary: { _eq: true } }, limit: 1) {
          id
          image {
            id
            url
          }
        }
      }
    }
  }
`);

const LeadDetailsAgentCardForAdmin = ({
  leadId,
  onAssignmentChange,
}: LeadDetailsAgentCardProps) => {
  const { t } = useLocale();

  return (
    <ListingAccordion
      title={t('Agent')}
      defaultExpanded
      disablePadding
      sx={{
        '& .MuiDataGrid-root': {
          border: 'none',
        },
      }}
    >
      <LeadAgents
        leadId={leadId}
        onChange={() => {
          onAssignmentChange?.(true);
        }}
      />
    </ListingAccordion>
  );
};

const LeadDetailsAgentCardForNonAdmin = ({
  leadId,
  onAssignmentChange,
}: LeadDetailsAgentCardProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const teamIds = useMemo(
    () => (me?.teams ?? []).map(team => team.team_id),
    [me?.teams],
  );

  const { data, loading, refetch } = useQuery(
    GET_LEAD_AGENT_SELECT_CARD_QUERY,
    {
      variables: { lead_id: leadId },
      skip: !leadId,
    },
  );
  const assignableTeamIds = useMemo(
    () => getAssignableLeadTeamIds(data?.leads_by_pk?.owner_team_id, teamIds),
    [data?.leads_by_pk?.owner_team_id, teamIds],
  );

  const visibleAgents = useMemo(
    () =>
      mergeVisibleLeadAgents(
        data?.lead_agents,
        me?.id,
        me?.user_ids_in_led_teams,
      ),
    [data?.lead_agents, me?.id, me?.user_ids_in_led_teams],
  );

  const primaryAgent = visibleAgents[0] ?? null;
  const isTeamLeader = (me?.user_ids_in_led_teams ?? []).length > 0;

  const { data: teamMembersData, loading: teamMembersLoading } = useQuery(
    GET_TEAM_MEMBERS,
    {
      variables: { team_ids: assignableTeamIds },
      skip: assignableTeamIds.length === 0,
    },
  );

  const teamMemberOptions = useMemo<UserSelectFragment[]>(() => {
    const entries = teamMembersData?.teams_users ?? [];
    if (entries.length === 0) {
      return [];
    }

    const unique = new Map<string, UserSelectFragment>();
    for (const entry of entries) {
      if (entry.user != null) {
        unique.set(entry.user.id, entry.user as UserSelectFragment);
      }
    }

    return Array.from(unique.values());
  }, [teamMembersData?.teams_users]);

  const userSelectFilters = useMemo<Users_Bool_Exp | undefined>(() => {
    return {
      is_broker: { _eq: true },
      teams_users: {
        team_id: { _in: assignableTeamIds },
        deactivated: { _eq: false },
      },
    };
  }, [assignableTeamIds]);

  const [getAssignStage] = useLazyQuery(GET_LEAD_ASSIGN_STAGE_QUERY, {});
  const [getUserTeams] = useLazyQuery(GET_USER_TEAMS, {});
  const [isSelectingNewAgent, setIsSelectingNewAgent] = useState(false);
  const [pendingAgent, setPendingAgent] = useState<UserSelectFragment | null>(
    null,
  );

  // Warning shown before assigning. An accepted prospection-map assignment
  // of the viewer (or of someone they lead) survives a reassignment, so it
  // keeps the lead visible.
  const keepsProspectionAssignment =
    data?.lead_agents.some(
      agent =>
        agent.source === Lead_Agent_Source_Enum_Enum.ProspectionMap &&
        agent.passed_at == null &&
        isViewerOrLedUser(me?.id, me?.user_ids_in_led_teams, agent.user?.id),
    ) ?? false;

  const willLoseAccess =
    pendingAgent != null &&
    !keepsProspectionAssignment &&
    !isViewerOrLedUser(me?.id, me?.user_ids_in_led_teams, pendingAgent.id);

  const [assignLead, { loading: isAssigningAgent }] = useMutation(
    ASSIGN_BROKER,
    {
      refetchQueries: [
        GET_LEADS,
        GET_LEAD_AGENT_STATUS,
        GET_LEAD_DETAILS,
        GET_LEAD_AGENT_SELECT_CARD_QUERY,
      ],
      awaitRefetchQueries: true,
      onError: setErrorLike,
    },
  );

  const handleUserSelected = useCallback(
    async (value: string | null) => {
      if (value == null || value === primaryAgent?.user?.id) {
        setIsSelectingNewAgent(false);
        return;
      }

      if (primaryAgent == null) {
        return;
      }

      const [{ data: userTeams }, { data: assignStage }] = await Promise.all([
        getUserTeams({ variables: { userId: value } }),
        getAssignStage({
          variables: {
            pipeline_id: primaryAgent.lead_stage?.pipeline_id ?? '',
          },
        }),
      ]);

      const teamIdsForNewUser =
        userTeams?.teams_users.map(tu => tu.team_id) ?? [];

      const { data: assignment } = await assignLead({
        variables: {
          lead_id: leadId,
          broker_id: value,
          team_ids: teamIdsForNewUser,
          stage_id: assignStage?.lead_stages?.[0]?.id,
          reassignment: createResultPageReassignment({
            leadId,
            replacementBrokerId: value,
            replacementTeamIds: teamIdsForNewUser,
          }),
          keep_tasks: keepsProspectionAssignmentTask({
            leadId,
            brokerId: value,
          }),
          activity_object: {
            lead_id: leadId,
            activity_type: Activity_Types_Enum_Enum.Assignment,
            done: false,
            due_at: 'now()',
            assigned_to: value,
            ...(me?.is_admin ? { created_by: me?.id } : {}),
          },
        },
      });

      emitFeedRefresh();

      if (assignment != null) {
        let keepOpen = me?.id === value;
        if (!keepOpen && isTeamLeader) {
          const { data: afterAssignment } = await refetch();
          keepOpen =
            afterAssignment?.leads_by_pk != null ||
            isViewerOrLedUser(me?.id, me?.user_ids_in_led_teams, value);
        }
        onAssignmentChange?.(keepOpen);
      }
      setIsSelectingNewAgent(false);
    },
    [
      primaryAgent,
      refetch,
      isTeamLeader,
      assignLead,
      getAssignStage,
      getUserTeams,
      leadId,
      me?.id,
      me?.is_admin,
      me?.user_ids_in_led_teams,
      onAssignmentChange,
    ],
  );

  return (
    <ListingAccordion title={t('Agent')} defaultExpanded>
      {loading ? (
        <LoadingSpinner sx={{ p: 0 }} />
      ) : primaryAgent != null ? (
        <Stack spacing={2}>
          <Stack spacing={1.5}>
            {visibleAgents.map(agent => (
              <Stack
                key={agent.user?.id ?? agent.id}
                direction="row"
                spacing={1.5}
                sx={{
                  alignItems: 'center',
                }}
              >
                <UserAvatar user={agent.user ?? null} size={48} />
                <Stack spacing={0.25}>
                  <Typography
                    variant="subtitle1"
                    noWrap
                    sx={{ lineHeight: 1.3 }}
                  >
                    {agent.user
                      ? getUserDisplayName(agent.user)
                      : t('No agent')}
                  </Typography>
                  {agent.user?.default_team?.name && (
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {agent.user.default_team.name}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            ))}
          </Stack>

          {isSelectingNewAgent ? (
            teamMembersLoading ? (
              <LoadingSpinner sx={{ p: 0, flexGrow: 1 }} />
            ) : (
              <UserSelect
                autoFocus
                limit={20}
                onSelectedUsersChange={selected => {
                  if (Array.isArray(selected)) {
                    return;
                  }
                  if (
                    selected == null ||
                    selected.id === primaryAgent?.user?.id
                  ) {
                    setIsSelectingNewAgent(false);
                    return;
                  }
                  setPendingAgent(selected);
                  setIsSelectingNewAgent(false);
                }}
                onClose={(_, reason) => {
                  if (reason !== 'selectOption') {
                    setIsSelectingNewAgent(false);
                  }
                }}
                filters={userSelectFilters}
                defaultOptions={teamMemberOptions}
                openOnMount
                disabled={isAssigningAgent}
                sx={{ flexGrow: 1 }}
              />
            )
          ) : (
            <Button
              variant="soft"
              startIcon={<RefreshCw />}
              onClick={() => setIsSelectingNewAgent(true)}
              disabled={isAssigningAgent}
            >
              {t('Reassign')}
            </Button>
          )}
        </Stack>
      ) : (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('No agent assigned yet')}
        </Typography>
      )}
      <ConfirmDialog
        open={pendingAgent != null}
        title={t('Reassign this lead to {{name}}?', {
          name:
            pendingAgent?.full_name ??
            pendingAgent?.primaryEmail?.[0]?.email ??
            '',
        })}
        isSubmitting={isAssigningAgent}
        okText={t('Reassign')}
        onClose={() => setPendingAgent(null)}
        onSubmit={async () => {
          if (pendingAgent == null) {
            return;
          }
          await handleUserSelected(pendingAgent.id);
          setPendingAgent(null);
        }}
      >
        <DialogContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
              <UserAvatar user={pendingAgent} size={48} />
              <Stack spacing={0.25}>
                <Typography variant="subtitle1" noWrap sx={{ lineHeight: 1.3 }}>
                  {pendingAgent?.full_name ?? ''}
                </Typography>
                {pendingAgent?.primaryEmail?.[0]?.email && (
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ color: 'text.secondary' }}
                  >
                    {pendingAgent.primaryEmail[0].email}
                  </Typography>
                )}
              </Stack>
            </Stack>
            {willLoseAccess && (
              <Alert severity="warning">
                {t('You will lose access to this lead after reassigning it')}
              </Alert>
            )}
          </Stack>
        </DialogContent>
      </ConfirmDialog>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </ListingAccordion>
  );
};

export const LeadDetailsAgentCard = ({
  leadId,
  onAssignmentChange,
}: LeadDetailsAgentCardProps) => {
  const { me } = useAppData();
  const isAdmin = me?.is_admin === true;

  if (isAdmin) {
    return (
      <LeadDetailsAgentCardForAdmin
        leadId={leadId}
        onAssignmentChange={onAssignmentChange}
      />
    );
  }

  return (
    <LeadDetailsAgentCardForNonAdmin
      leadId={leadId}
      onAssignmentChange={onAssignmentChange}
    />
  );
};
